// /mirror — Family-Mirror UI.
// Shows: pick viewer -> consent confirm -> active-share indicator (countdown + reconfirm + end).

import type { RouteRenderer } from "../types.ts";
import {
  startMirrorSession,
  getActiveSessionId,
  readMirrorAudit,
  type MirrorSession,
  type MirrorOfferPayload,
  type MirrorAnswerPayload,
} from "../chassis/elder/family-mirror.ts";
import { makeBilingual, getSelectedLang, screenReaderAnnounce } from "../chassis/elder/index.ts";
import { i18n } from "../chassis/elder/i18n-strings.ts";
import {
  loadState as loadOnboardingState,
  type FamilyContact,
} from "../chassis/elder/onboarding.ts";
import { navigate } from "../router.ts";

export const renderMirror: RouteRenderer = (host) => {
  const lang = getSelectedLang();
  const shell = document.createElement("div");
  shell.className = "sarathi-route-shell";

  const heading = document.createElement("h1");
  heading.textContent = "परिवार दर्पण";
  const sub = document.createElement("span");
  sub.className = "secondary";
  sub.textContent = "Family Mirror — share your screen with a child you trust";
  const headerEl = document.createElement("header");
  const back = makeBackButton(lang);
  const titleWrap = document.createElement("div");
  titleWrap.appendChild(heading);
  titleWrap.appendChild(sub);
  headerEl.appendChild(back);
  headerEl.appendChild(titleWrap);
  shell.appendChild(headerEl);

  const statusCard = document.createElement("div");
  statusCard.className = "sarathi-mirror-status";
  shell.appendChild(statusCard);

  let activeSession: MirrorSession | null = null;
  let countdownTimer: number | null = null;

  const renderStatus = (): void => {
    const activeId = getActiveSessionId();
    if (activeSession || activeId) {
      statusCard.dataset.active = "true";
      statusCard.innerHTML = `
        <span class="sarathi-pill" data-tone="live">${makeBilingual(i18n, "mirror.session.live", lang).primary}</span>
        <h2 style="font-family:'Crimson Pro',serif;font-size:24px;margin:14px 0 6px">${activeSession?.viewerLabel ?? "Family viewer"}</h2>
        <div id="sarathi-mirror-countdown" style="font-size:18px;color:var(--sarathi-ash);margin-bottom:18px"></div>
        <div class="sarathi-row">
          <button type="button" id="sarathi-mirror-reconfirm" style="min-height:88px;background:transparent;color:var(--sarathi-ink);border:3px solid var(--sarathi-ink);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer">+ 15 min</button>
          <button type="button" id="sarathi-mirror-end" style="min-height:88px;background:var(--sarathi-danger);color:#fff;border:3px solid #6e0a04;border-radius:14px;font-size:20px;font-weight:700;cursor:pointer">रोकें · Stop sharing</button>
        </div>
      `;
      const cd = statusCard.querySelector<HTMLElement>("#sarathi-mirror-countdown");
      const tickCountdown = (): void => {
        if (!activeSession || !cd) return;
        const elapsed = Date.now() - activeSession.startedAt;
        const remaining = Math.max(0, activeSession.maxDurationMs - elapsed);
        const mins = Math.floor(remaining / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        cd.textContent = `${mins} min ${secs.toString().padStart(2, "0")} sec baki — auto stop`;
      };
      tickCountdown();
      if (countdownTimer !== null) clearInterval(countdownTimer);
      countdownTimer = window.setInterval(tickCountdown, 1000);
      statusCard.querySelector("#sarathi-mirror-reconfirm")?.addEventListener("click", () => {
        activeSession?.reconfirm();
        screenReaderAnnounce("15 minute extension applied", { polite: true, clearAfterMs: 4000 });
      });
      statusCard.querySelector("#sarathi-mirror-end")?.addEventListener("click", () => {
        void activeSession?.end("user-ended").then(() => {
          activeSession = null;
          renderStatus();
        });
      });
    } else {
      statusCard.dataset.active = "false";
      statusCard.innerHTML = `
        <h2 style="font-family:'Crimson Pro',serif;font-size:24px;margin:0 0 8px">कोई साझा नहीं चल रहा</h2>
        <p style="font-size:18px;color:var(--sarathi-ash);margin:0 0 12px">No active share. Pick a family member below to start.</p>
      `;
      if (countdownTimer !== null) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    }
  };
  renderStatus();

  // Family contact picker
  const onboarding = loadOnboardingState();
  const eligibleContacts = onboarding.familyContacts.filter((c) => c.canMirror);
  const pickerCard = document.createElement("div");
  pickerCard.className = "sarathi-card";
  pickerCard.innerHTML = `<h2>किसके साथ साझा करें?</h2><span class="sarathi-secondary-label">Choose family member to share with</span>`;

  if (eligibleContacts.length === 0) {
    const empty = document.createElement("p");
    empty.style.cssText = "margin:14px 0;color:var(--sarathi-ash);font-size:18px";
    empty.textContent = "Aap ne abhi tak parivar ke kisi sadasya ko 'share screen' anumati nahin di. Sarathi setup poora kijiye.";
    pickerCard.appendChild(empty);
    const setupBtn = document.createElement("button");
    setupBtn.type = "button";
    setupBtn.style.cssText = "min-height:88px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer;padding:0 24px";
    setupBtn.textContent = "Setup khol kar contacts jodain";
    setupBtn.addEventListener("click", () => navigate("onboard"));
    pickerCard.appendChild(setupBtn);
  } else {
    const list = document.createElement("ul");
    list.className = "sarathi-list";
    list.style.marginTop = "14px";
    for (const c of eligibleContacts) {
      const li = document.createElement("li");
      const info = document.createElement("div");
      info.innerHTML = `<div style="font-size:22px;font-weight:700">${escapeHtml(c.name)}</div><div style="font-size:16px;color:var(--sarathi-ash);font-weight:500">${escapeHtml(c.relation)} · ${escapeHtml(c.phone)}</div>`;
      const startBtn = document.createElement("button");
      startBtn.type = "button";
      startBtn.style.cssText = "min-height:72px;padding:0 22px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:18px;font-weight:700;cursor:pointer";
      startBtn.textContent = "Share screen";
      startBtn.addEventListener("click", () => {
        void initiateShare(c);
      });
      li.appendChild(info);
      li.appendChild(startBtn);
      list.appendChild(li);
    }
    pickerCard.appendChild(list);
  }
  shell.appendChild(pickerCard);

  // Audit log
  const audit = readMirrorAudit();
  const auditCard = document.createElement("div");
  auditCard.className = "sarathi-card";
  auditCard.innerHTML = `<h2>हाल का इतिहास</h2><span class="sarathi-secondary-label">Recent share history</span>`;
  if (audit.length === 0) {
    const empty = document.createElement("p");
    empty.style.cssText = "margin:14px 0;color:var(--sarathi-ash);font-size:17px";
    empty.textContent = "No prior sessions logged.";
    auditCard.appendChild(empty);
  } else {
    const list = document.createElement("ul");
    list.className = "sarathi-list";
    list.style.marginTop = "14px";
    for (const entry of audit.slice(-8).reverse()) {
      const li = document.createElement("li");
      const when = new Date(entry.startedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
      const dur = entry.durationMs !== null ? `${Math.round(entry.durationMs / 60000)} min` : "(ongoing)";
      li.innerHTML = `
        <div>
          <div style="font-size:18px;font-weight:600">${escapeHtml(entry.viewerLabel)}</div>
          <div style="font-size:15px;color:var(--sarathi-ash)">${escapeHtml(when)} · ${escapeHtml(dur)} · ${escapeHtml(entry.endReason)}</div>
        </div>
      `;
      list.appendChild(li);
    }
    auditCard.appendChild(list);
  }
  shell.appendChild(auditCard);

  host.appendChild(shell);

  async function initiateShare(viewer: FamilyContact): Promise<void> {
    try {
      const session = await startMirrorSession({
        viewerLabel: viewer.name,
        lang,
        host: document.body,
        onOffer: stubSignalingExchange,
      });
      if (!session) {
        screenReaderAnnounce("Share cancelled or denied", { polite: true, clearAfterMs: 4000 });
        renderStatus();
        return;
      }
      activeSession = session;
      session.onRemoteEnd(() => {
        activeSession = null;
        renderStatus();
      });
      renderStatus();
    } catch (err) {
      console.error("[sarathi.mirror] failed to start", err);
      alert("Screen sharing failed to start. Try again or check browser permissions.");
    }
  }

  return () => {
    if (countdownTimer !== null) clearInterval(countdownTimer);
    if (activeSession) void activeSession.end("user-ended");
    shell.remove();
  };
};

// v1 signaling: copy-paste flow (no central server).
// Parent generates offer → shows QR or short code → child pastes answer back.
// For prototype we accept a quick clipboard paste of the answer SDP.
async function stubSignalingExchange(offer: MirrorOfferPayload): Promise<MirrorAnswerPayload> {
  const dialog = document.createElement("div");
  dialog.setAttribute("role", "dialog");
  dialog.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.75);display:flex;align-items:center;justify-content:center;z-index:99999;padding:24px;font-family:'Mukta',serif";
  const card = document.createElement("div");
  card.style.cssText = "background:#fffaeb;border:3px solid #1c1a16;border-radius:18px;padding:28px;max-width:520px;width:100%";
  const offerB64 = btoa(unescape(encodeURIComponent(JSON.stringify(offer))));
  card.innerHTML = `
    <h2 style="font-size:24px;font-weight:700;margin:0 0 8px">Send this code to your child</h2>
    <p style="font-size:16px;color:#5a554d;margin:0 0 16px">Family member opens https://sarathi.yourtube.in/mirror/join#${offerB64.slice(0, 60)}... and pastes the response back.</p>
    <textarea readonly style="width:100%;height:90px;font-family:monospace;font-size:12px;border:1px solid #d4c8a0;border-radius:8px;padding:10px">${offerB64}</textarea>
    <label style="display:block;margin:18px 0 8px;font-weight:700;font-size:18px">Paste their response here:</label>
    <textarea id="sarathi-answer-input" style="width:100%;height:90px;font-family:monospace;font-size:12px;border:2px solid #1c1a16;border-radius:8px;padding:10px" placeholder="Paste answer SDP code..."></textarea>
    <div style="display:flex;gap:14px;margin-top:18px">
      <button id="sarathi-answer-submit" type="button" style="flex:1;min-height:72px;background:#b85410;color:#fff;border:3px solid #8e3e08;border-radius:14px;font-size:20px;font-weight:700;cursor:pointer">Connect</button>
      <button id="sarathi-answer-cancel" type="button" style="flex:1;min-height:72px;background:transparent;color:#1c1a16;border:3px solid #1c1a16;border-radius:14px;font-size:20px;font-weight:700;cursor:pointer">Cancel</button>
    </div>
  `;
  dialog.appendChild(card);
  document.body.appendChild(dialog);
  return new Promise<MirrorAnswerPayload>((resolve, reject) => {
    const submitBtn = card.querySelector<HTMLButtonElement>("#sarathi-answer-submit");
    const cancelBtn = card.querySelector<HTMLButtonElement>("#sarathi-answer-cancel");
    const input = card.querySelector<HTMLTextAreaElement>("#sarathi-answer-input");
    submitBtn?.addEventListener("click", () => {
      const raw = input?.value.trim() ?? "";
      try {
        const decoded = decodeURIComponent(escape(atob(raw)));
        const parsed = JSON.parse(decoded) as { sdp: string };
        dialog.remove();
        resolve({ sdp: parsed.sdp, receivedAt: Date.now() });
      } catch {
        alert("That code does not look correct. Please paste again.");
      }
    });
    cancelBtn?.addEventListener("click", () => {
      dialog.remove();
      reject(new Error("user-cancelled"));
    });
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
    return map[c] ?? c;
  });
}

function makeBackButton(_lang: ReturnType<typeof getSelectedLang>): HTMLElement {
  const back = document.createElement("button");
  back.type = "button";
  back.style.cssText = "min-width:88px;min-height:88px;font-size:32px;background:transparent;border:3px solid var(--sarathi-ink);border-radius:14px;cursor:pointer;font-weight:700";
  back.textContent = "←";
  back.addEventListener("click", () => navigate("home"));
  return back;
}
